import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { formatChangeDate } from './formatChangeDate';
import PanelTooltip from './PanelTooltip';

const FIELD_VERBS = { create: 'added', update: 'updated', delete: 'removed' };
const SETTING_VERBS = { create: 'created', update: 'updated', delete: 'deleted' };

const verbOf = (verbs, op) => verbs[op] ?? verbs.update;

const offerTitle = (label, noun, verb) =>
  label ? `${label} ${noun} ${verb}` : `${noun.charAt(0).toUpperCase()}${noun.slice(1)} ${verb}`;

// Rows that stand for one record, titled by its name when it has one.
const RECORD_TITLES = {
  field: (label, op) =>
    label ? `${label} field ${verbOf(FIELD_VERBS, op)}` : `Field ${verbOf(FIELD_VERBS, op)}`,
  coupon: (label, op) =>
    label
      ? `${label} discount code ${verbOf(SETTING_VERBS, op)}`
      : `Discount code ${verbOf(SETTING_VERBS, op)}`,
  taxRate: (_label, op) => `Tax rate ${verbOf(SETTING_VERBS, op)}`,
  ticketType: (label, op) =>
    label
      ? `${label} ticket type ${verbOf(SETTING_VERBS, op)}`
      : `Ticket type ${verbOf(SETTING_VERBS, op)}`,
  // Themes the AI makes are named "<page title> theme".
  theme: (label, op) => {
    if (!label) return `Theme ${verbOf(SETTING_VERBS, op)}`;
    return /\btheme$/i.test(label)
      ? `${label} ${verbOf(SETTING_VERBS, op)}`
      : `${label} theme ${verbOf(SETTING_VERBS, op)}`;
  },
  // A bump is created as a product, then added to the page.
  orderBump: (label, op, { noun }) => offerTitle(label, noun, verbOf(FIELD_VERBS, op)),
  orderBumpProduct: (label, op, { noun }) => offerTitle(label, noun, verbOf(SETTING_VERBS, op)),
};

// The editor's wording for coupon, tax rate, ticket type and order bump settings; field details read
// well humanized.
const DETAIL_LABELS = {
  coupon: {
    discount: 'Discount',
    duration: 'Subscription duration',
    maxRedemptions: 'Max uses',
    redeemBy: 'Expires on',
    pages: 'Applies to',
    ticketTypes: 'Tickets',
  },
  taxRate: { displayName: 'Tax name', percentage: 'Tax rate', inclusive: 'Tax behavior' },
  orderBump: {
    preselected: 'Preselect',
    allowQuantity: 'Allow quantity',
    showHideLogic: 'Conditional logic',
    hidden: 'Hide',
    shortenDescription: 'Show more/less',
  },
  orderBumpProduct: {
    title: 'Product title',
    shortDescription: 'Short description',
    headingText: 'Heading',
    callToActionText: 'Call to action',
    calloutText: 'Call out',
    calloutIcon: 'Call out icon',
    imageIds: 'Image',
  },
  ticketType: {
    group: 'Ticket group',
    price: 'Price',
    discountedFromPrice: 'Discounted from',
    feeAmount: 'Booking fee',
    saleStartOn: 'Start sale on',
    saleEndOn: 'End sale on',
    capacity: 'Quantity available',
    minQuantity: 'Min per order',
    maxQuantity: 'Max per order',
    hidden: 'Visibility',
  },
};

function humanizeKey(key) {
  const words = String(key)
    .replace(/\./g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// An object the API sent no summary for, which reads as code if shown as it is.
const isUnsummarized = value =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  typeof value.count !== 'number';

// Values are rendered as text nodes only: decoded summaries can contain markup.
function formatChangeValue(value) {
  if (value === null || value === undefined || value === '') return 'None';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (Array.isArray(value)) return value.length ? value.map(formatChangeValue).join(', ') : 'None';
  if (isUnsummarized(value)) return 'Updated';
  if (typeof value === 'object') return `${value.count} ${value.count === 1 ? 'item' : 'items'}`;
  return formatChangeDate(value) ?? String(value);
}

const isShortenable = value =>
  (typeof value === 'string' && value !== '') || (Array.isArray(value) && value.length > 0);

// The API cuts long text and long name lists without marking where.
function formatChangeSide(value, truncated) {
  const text = formatChangeValue(value);
  return truncated && isShortenable(value) ? `${text}…` : text;
}

const isRecordRow = row => Object.hasOwn(RECORD_TITLES, row.key ?? '');

function changeRowTitle(row) {
  if (isRecordRow(row)) return RECORD_TITLES[row.key](row.label, row.op, row);
  if (row.location.label) return row.location.label;
  return row.key ? humanizeKey(row.key) : 'Changed (details unavailable)';
}

const SIDE_CLASS = 'line-clamp-2 whitespace-pre-wrap break-words';

function ValueChange({ label, before, after, truncated }) {
  const hasBefore = before !== undefined && !(isUnsummarized(before) && isUnsummarized(after));
  const hasAfter = after !== undefined;
  if (!hasBefore && !hasAfter) return null;

  return (
    <div className="flex flex-wrap gap-x-1.5 text-xs leading-[17px] text-muted-foreground">
      {label && <span className="shrink-0">{label}:</span>}
      {hasBefore && (
        <del className={SIDE_CLASS}>
          <span className="sr-only">Before: </span>
          <span>{formatChangeSide(before, truncated)}</span>
        </del>
      )}
      {hasAfter && (
        <span className={SIDE_CLASS}>
          {hasBefore && <span aria-hidden="true">→ </span>}
          <span className="sr-only">After: </span>
          <span>{formatChangeSide(after, truncated)}</span>
        </span>
      )}
    </div>
  );
}

// A deleted record's details are what it had, so they read as struck out.
function RecordDetails({ row }) {
  return row.details.map(detail => (
    <ValueChange
      key={detail.key}
      label={detail.label ?? DETAIL_LABELS[row.key]?.[detail.key] ?? humanizeKey(detail.key)}
      before={row.op === 'delete' || row.op === 'update' ? detail.before : undefined}
      after={row.op === 'delete' ? undefined : detail.after}
      truncated={detail.truncated}
    />
  ));
}

function OpenButton({ label, onClick }) {
  return (
    <PanelTooltip label={label}>
      <Button variant="outline" size="icon-xs" aria-label={label} onClick={onClick}>
        <ArrowRight aria-hidden="true" />
      </Button>
    </PanelTooltip>
  );
}

export default function ChangeRow({ row, onShow, onOpen }) {
  const { tab, section, route, path, target } = row.location;
  const title = changeRowTitle(row);

  return (
    <li className="flex flex-col gap-1 border-t py-2.5 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        <div className="min-w-0 flex-1 basis-28">
          <div className="break-words text-[13px] font-medium leading-[18px]">{title}</div>
          <div className="break-words text-xs leading-[17px] text-muted-foreground">
            {section && section !== tab ? `${tab} › ${section}` : tab}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          {/* A deleted record is gone from the preview, so there is nothing to show. */}
          {onShow && target && row.op !== 'delete' && (
            <Button
              variant="ghost"
              size="xs"
              className="text-muted-foreground"
              aria-label={`Show ${title}`}
              onClick={() => onShow(row)}
            >
              Show
            </Button>
          )}
          {(route || path) && onOpen && (
            <OpenButton
              label={`Open ${section ?? tab}`}
              onClick={() =>
                onOpen(row.focus ? { ...row.location, focus: row.focus } : row.location)
              }
            />
          )}
        </div>
      </div>
      {row.captureFailed ? (
        row.key !== null && (
          <div className="text-xs leading-[17px] text-muted-foreground">
            Changed (details unavailable)
          </div>
        )
      ) : isRecordRow(row) ? (
        <RecordDetails row={row} />
      ) : (
        <ValueChange before={row.before} after={row.after} truncated={row.truncated} />
      )}
    </li>
  );
}
